import api from '@/api'
import { message } from 'ant-design-vue'

const storyHub = {
  namespaced: true,

  state: {
    // Posted stories data
    postedStories: [],
    postedStoriesTotal: 0,
    postedStoriesLoading: false,

    // Highlight assets
    highlightAssets: [],
    highlightAssetsTotal: 0,

    filters: {
      station: '',
      search: '',
      story_type: []
    },
    pagination: {
      posted: {
        page: 1,
        pageSize: 20,
        total: 0
      }
    },

    // UI state
    loading: false,
    error: null,
    lastUpdated: null,

    // Current selections
    selectedStory: null,
    selectedMediaAsset: null,

    // Modal states
    postingModalVisible: false,
    scriptEditorVisible: false,
    mediaHighlightModalVisible: false
  },

  getters: {
    // Posted stories getters
    postedStories: (state) => state.postedStories,
    postedStoriesCount: (state) => state.postedStories.length,
    postedStoriesTotal: (state) => state.postedStoriesTotal,
    hasMorePostedStories: (state) => state.postedStories.length < state.postedStoriesTotal,

    // Stories with highlights
    storiesWithHighlights: (state) => {
      return state.postedStories.filter((story) => story.media_assets && story.media_assets.some((asset) => asset.is_highlight))
    },
    storiesWithHighlightsCount: (state, getters) => getters.storiesWithHighlights.length,

    filteredPostedStories: (state) => {
      let stories = state.postedStories

      if (state.filters.search) {
        const searchTerm = state.filters.search.toLowerCase()
        stories = stories.filter((story) => {
          if (story.title && story.title.toLowerCase().includes(searchTerm)) return true
          // Summary is the oldest comment
          const comments = story.comments
          if (comments?.length) {
            const oldest = [...comments].sort((a, b) => new Date(a.comment_date) - new Date(b.comment_date))[0]
            if (oldest?.comment?.toLowerCase().includes(searchTerm)) return true
          }
          return false
        })
      }

      if (state.filters.station) {
        stories = stories.filter((story) => story.station === state.filters.station)
      }

      if (state.filters.story_type && state.filters.story_type.length > 0) {
        stories = stories.filter((story) => state.filters.story_type.includes(story.story_type))
      }

      return stories
    },

    isLoading: (state) => state.loading || state.postedStoriesLoading,

    // Story lookup
    getStoryById: (state) => (id) => {
      return state.postedStories.find((story) => story.id === id)
    }
  },

  mutations: {
    // Posted stories mutations
    SET_POSTED_STORIES(state, { stories, total }) {
      state.postedStories = stories
      state.postedStoriesTotal = total
      state.pagination.posted.total = total
    },

    APPEND_POSTED_STORIES(state, { stories, total }) {
      const existingIds = new Set(state.postedStories.map((s) => s.id))
      const newStories = stories.filter((s) => !existingIds.has(s.id))
      state.postedStories = [...state.postedStories, ...newStories]
      state.postedStoriesTotal = total
      state.pagination.posted.total = total
    },

    SET_POSTED_STORIES_LOADING(state, loading) {
      state.postedStoriesLoading = loading
    },

    // Add/update/remove individual stories
    ADD_POSTED_STORY(state, story) {
      const existingIndex = state.postedStories.findIndex((s) => s.id === story.id)
      if (existingIndex >= 0) {
        state.postedStories.splice(existingIndex, 1, story)
      } else {
        state.postedStories.unshift(story)
      }
    },

    REMOVE_POSTED_STORY(state, storyId) {
      const index = state.postedStories.findIndex((s) => s.id === storyId)
      if (index >= 0) {
        state.postedStories.splice(index, 1)
      }
    },

    // Update story script
    UPDATE_STORY_SCRIPT(state, { storyId, script }) {
      const story = state.postedStories.find((s) => s.id === storyId)
      if (story) {
        story.script = script
        story.updated_at = new Date().toISOString()
      }
    },

    // Media asset highlight mutations
    SET_MEDIA_HIGHLIGHT(state, { storyId, mediaAssetId }) {
      const story = state.postedStories.find((s) => s.id === storyId)
      if (story && story.media_assets) {
        story.media_assets.forEach((asset) => {
          asset.is_highlight = asset.id === mediaAssetId
        })
      }
    },

    SET_MEDIA_HIGHLIGHTS(state, { storyId, mediaAssetIds }) {
      const story = state.postedStories.find((s) => s.id === storyId)
      if (!story) return
      if (story.media_assets) {
        const idSet = new Set(mediaAssetIds)
        story.media_assets.forEach((asset) => {
          asset.is_highlight = idSet.has(asset.id)
        })
      }
      story.highlight_count = mediaAssetIds.length
    },

    // Filter mutations
    SET_FILTER(state, { key, value }) {
      state.filters[key] = value
    },

    RESET_FILTERS(state) {
      state.filters = {
        station: '',
        search: '',
        story_type: []
      }
    },

    // Pagination mutations
    SET_PAGINATION(state, { type, pagination }) {
      state.pagination[type] = { ...state.pagination[type], ...pagination }
    },

    // UI state mutations
    SET_LOADING(state, loading) {
      state.loading = loading
    },

    SET_ERROR(state, error) {
      state.error = error
    },

    SET_LAST_UPDATED(state, timestamp) {
      state.lastUpdated = timestamp || new Date().toISOString()
    },

    // Selection mutations
    SET_SELECTED_STORY(state, story) {
      state.selectedStory = story
    },

    SET_SELECTED_MEDIA_ASSET(state, asset) {
      state.selectedMediaAsset = asset
    },

    // Modal state mutations
    SET_POSTING_MODAL_VISIBLE(state, visible) {
      state.postingModalVisible = visible
    },

    SET_SCRIPT_EDITOR_VISIBLE(state, visible) {
      state.scriptEditorVisible = visible
    },

    SET_MEDIA_HIGHLIGHT_MODAL_VISIBLE(state, visible) {
      state.mediaHighlightModalVisible = visible
    }
  },

  actions: {
    // Fetch posted stories. Pass { append: true } to append instead of replacing
    // (used by infinite scroll).
    async fetchPostedStories({ commit, state }, params = {}) {
      commit('SET_POSTED_STORIES_LOADING', true)
      commit('SET_ERROR', null)

      const { append = false, ...filterParams } = params
      const queryParams = {
        limit: state.pagination.posted.pageSize,
        offset: append ? state.postedStories.length : 0,
        ...filterParams
      }

      try {
        const response = await api.storyHub.getPostedStories(queryParams)

        const stories = response.data.posted_stories.stories || response.data.posted_stories
        const total = response.data.posted_stories.total ?? stories.length

        commit(append ? 'APPEND_POSTED_STORIES' : 'SET_POSTED_STORIES', { stories, total })
        commit('SET_LAST_UPDATED')

        return response.data
      } catch (error) {
        console.error('Error fetching posted stories:', error)
        commit('SET_ERROR', error.message || 'Failed to fetch posted stories')
        message.error('Failed to load posted stories')
        throw error
      } finally {
        commit('SET_POSTED_STORIES_LOADING', false)
      }
    },

    async postStory({ commit }, { storyId, highlightAssetIds = [], scheduledPostAt = null }) {
      try {
        const postData = scheduledPostAt ? { scheduled_post_at: scheduledPostAt } : {}
        const response = await api.storyHub.postStory(storyId, postData)

        if (highlightAssetIds.length > 0) {
          await Promise.all(highlightAssetIds.map((id) => api.storyHub.setMediaAssetAsHighlight(id)))
        }

        if (scheduledPostAt) {
          commit('PATCH_STORY', { id: storyId, fields: { scheduled_post_at: scheduledPostAt } }, { root: true })
          commit('SET_LAST_UPDATED')
          message.success('Story scheduled for StoryHub')
          return response.data
        }

        const story = response.data.story
        commit('ADD_POSTED_STORY', story)
        commit('PATCH_STORY', { id: storyId, fields: { is_posted: true, posted_at: story?.posted_at } }, { root: true })

        if (highlightAssetIds.length > 0) {
          commit('SET_MEDIA_HIGHLIGHTS', { storyId, mediaAssetIds: highlightAssetIds })
        }

        commit('SET_LAST_UPDATED')

        message.success('Story posted to StoryHub')
        return response.data
      } catch (error) {
        console.error('Error posting story:', error)
        message.error('Failed to post story to StoryHub')
        throw error
      }
    },

    // Unpost story from StoryHub
    async unpostStory({ commit }, storyId) {
      try {
        await api.storyHub.unpostStory(storyId)

        commit('REMOVE_POSTED_STORY', storyId)
        commit('SET_LAST_UPDATED')

        message.success('Story removed from StoryHub')
      } catch (error) {
        console.error('Error unposting story:', error)
        message.error('Failed to remove story from StoryHub')
        throw error
      }
    },

    async notifyStory(_, storyId) {
      try {
        const response = await api.storyHub.notifyStory(storyId)
        message.success('Story Hub notification sent')
        return response.data
      } catch (error) {
        if (error.response?.status === 429) {
          message.warning('A notification for this story was already sent recently. Please wait before sending another.')
        } else {
          message.error('Failed to send Story Hub notification')
        }
        throw error
      }
    },

    async postMediaAsset({ commit }, mediaAssetId) {
      try {
        const response = await api.storyHub.postMediaAsset(mediaAssetId)

        const story = response.data.story
        commit('ADD_POSTED_STORY', story)
        commit('SET_LAST_UPDATED')

        message.success('Media asset posted to StoryHub')
        return response.data
      } catch (error) {
        console.error('Error posting media asset:', error)
        message.error('Failed to post media asset')
        throw error
      }
    },

    // Set media asset as highlight
    async setMediaAssetAsHighlight({ commit }, mediaAssetId) {
      try {
        const response = await api.storyHub.setMediaAssetAsHighlight(mediaAssetId)

        const mediaAsset = response.data.media_asset
        commit('SET_MEDIA_HIGHLIGHT', {
          storyId: mediaAsset.story_id,
          mediaAssetId: mediaAsset.id
        })
        commit('SET_LAST_UPDATED')

        message.success('Media asset set as story highlight')
        return response.data
      } catch (error) {
        console.error('Error setting media asset as highlight:', error)
        message.error('Failed to set media asset as highlight')
        throw error
      }
    },

    // Update story script
    async updateStoryScript({ commit }, { storyId, script }) {
      try {
        const response = await api.stories.updateStoryScript(storyId, script)

        commit('UPDATE_STORY_SCRIPT', { storyId, script })
        commit('SET_LAST_UPDATED')

        message.success('Script updated successfully')
        return response.data
      } catch (error) {
        console.error('Error updating story script:', error)
        message.error('Failed to update script')
        throw error
      }
    },

    // Filter actions
    updateFilter({ commit }, { key, value }) {
      commit('SET_FILTER', { key, value })
    },

    resetFilters({ commit }) {
      commit('RESET_FILTERS')
    },

    // Pagination actions
    updatePagination({ commit }, { type, pagination }) {
      commit('SET_PAGINATION', { type, pagination })
    },

    // UI actions
    setSelectedStory({ commit }, story) {
      commit('SET_SELECTED_STORY', story)
    },

    setSelectedMediaAsset({ commit }, asset) {
      commit('SET_SELECTED_MEDIA_ASSET', asset)
    },

    // Modal actions
    showPostingModal({ commit }, story) {
      commit('SET_SELECTED_STORY', story)
      commit('SET_POSTING_MODAL_VISIBLE', true)
    },

    hidePostingModal({ commit }) {
      commit('SET_POSTING_MODAL_VISIBLE', false)
      commit('SET_SELECTED_STORY', null)
    },

    showScriptEditor({ commit }, story) {
      commit('SET_SELECTED_STORY', story)
      commit('SET_SCRIPT_EDITOR_VISIBLE', true)
    },

    hideScriptEditor({ commit }) {
      commit('SET_SCRIPT_EDITOR_VISIBLE', false)
      commit('SET_SELECTED_STORY', null)
    },

    showMediaHighlightModal({ commit }, story) {
      commit('SET_SELECTED_STORY', story)
      commit('SET_MEDIA_HIGHLIGHT_MODAL_VISIBLE', true)
    },

    hideMediaHighlightModal({ commit }) {
      commit('SET_MEDIA_HIGHLIGHT_MODAL_VISIBLE', false)
      commit('SET_SELECTED_STORY', null)
    },

    // Filter actions
    setFilter({ commit, dispatch }, { key, value }) {
      commit('SET_FILTER', { key, value })
      // Auto-refresh data when filters change
      dispatch('fetchPostedStories', { [key]: value })
    }
  }
}

export default storyHub
